import React, { useEffect, useState } from 'react';
import { X, History } from 'lucide-react';
import { listFirmMessageEdits } from '../../lib/db';

function fmt(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString();
}

function nameOf(u) {
  if (!u) return 'Unknown';
  return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || 'User';
}

/**
 * Modal showing every prior version of a firm message. Driven by the
 * firm_message_edits table populated by edit_firm_message RPC.
 *
 * Props:
 *   messageId  — uuid of the row to inspect
 *   currentBody — current body for the "Current version" header at the top
 *   onClose
 */
export default function EditHistoryModal({ messageId, currentBody, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!messageId) return;
    listFirmMessageEdits(messageId).then(({ data, error: err }) => {
      if (err) setError(err.message || 'Could not load history');
      else setRows(data || []);
    });
  }, [messageId]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={14} className="text-slate-500" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Edit history</span>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={14} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4 space-y-3">
          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
          {rows === null && !error && (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
          {rows && rows.length === 0 && !error && (
            <p className="text-sm text-slate-500">No prior versions — this message has not been edited.</p>
          )}

          {rows && rows.length > 0 && (
            <>
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-semibold mb-1">Current version</div>
                <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words">
                  {currentBody || <em className="text-slate-400">empty</em>}
                </p>
              </div>
              {rows.map((r) => (
                <div key={r.id} className="rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
                  <div className="flex items-baseline justify-between gap-2 mb-1">
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Replaced</span>
                    <span className="text-[11px] text-slate-500">
                      {fmt(r.edited_at)} by {nameOf(r.editor)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                    {r.prior_body || <em className="text-slate-400">empty</em>}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
